import { Resources } from 'constants/resources';

import { AxiosResponse } from 'axios';
import { axios } from 'config/axios';
import qs from 'qs';
import {
  EntitiesSummary,
  TransactionsAmountSummary,
  TransactionsDynamicSummary,
  TransactionsSuccessSummary,
} from 'types/embedded/dashboard';

export type DashboardPeriodParams = {
  periodFrom: number | null;
  periodTo: number | null;
  timezone: string | null;
};
export type DashboardFilterParams = {
  merchant: string[];
  provider?: string[];
  gateway?: string[];
};
export type DashboardCurrencyParams = {
  currency: string;
};

export type DashboardTransactionsSummaryParams = DashboardPeriodParams &
  DashboardFilterParams &
  DashboardCurrencyParams;

export type DashboardTransactionsSuccessSummaryParams = DashboardPeriodParams &
  DashboardFilterParams;

export type DashboardTransactionsDynamicSummaryParams = DashboardPeriodParams &
  DashboardFilterParams &
  DashboardCurrencyParams;

export type DashboardMerchantsSummaryParams = DashboardPeriodParams & DashboardFilterParams;
export type DashboardProvidersSummaryParams = DashboardPeriodParams & DashboardFilterParams;

export interface DashboardApi {
  getTransactionsSummary(
    params: DashboardTransactionsSummaryParams,
    signal?: AbortSignal,
  ): Promise<TransactionsAmountSummary>;
  getTransactionsSuccessSummary(
    params: DashboardTransactionsSuccessSummaryParams,
    signal?: AbortSignal,
  ): Promise<TransactionsSuccessSummary>;
  getTransactionsDynamicSummary(
    params: DashboardTransactionsDynamicSummaryParams,
    signal?: AbortSignal,
  ): Promise<TransactionsDynamicSummary>;
  getMerchantsSummary(
    params: DashboardMerchantsSummaryParams,
    signal?: AbortSignal,
  ): Promise<EntitiesSummary>;
  getProvidersSummary(
    params: DashboardProvidersSummaryParams,
    signal?: AbortSignal,
  ): Promise<EntitiesSummary>;
}

const paramsSerializer = (params: Record<string, unknown>) =>
  qs.stringify(params, { arrayFormat: 'indices', encode: false });

const getDashboardTransactionsSummary = async (
  params: DashboardTransactionsSummaryParams,
  signal?: AbortSignal,
): Promise<TransactionsAmountSummary> => {
  return axios
    .get(`${Resources.Dashboard}/executed_transactions_amount`, {
      params,
      paramsSerializer,
      signal,
    })
    .then((response: AxiosResponse) => response.data);
};

const getDashboardTransactionsSuccessSummary = async (
  params: DashboardTransactionsSuccessSummaryParams,
  signal?: AbortSignal,
): Promise<TransactionsSuccessSummary> => {
  return axios
    .get(`${Resources.Dashboard}/transaction_success_summary`, {
      params,
      paramsSerializer,
      signal,
    })
    .then((response: AxiosResponse) => response.data);
};

const getDashboardTransactionsDynamicSummary = async (
  params: DashboardTransactionsDynamicSummaryParams,
  signal?: AbortSignal,
): Promise<TransactionsDynamicSummary> => {
  return axios
    .get(`${Resources.Dashboard}/executed_transactions_amount_by_steps?`, {
      params,
      paramsSerializer,
      signal,
    })
    .then((response: AxiosResponse) => response.data);
};

const getDashboardMerchantsSummary = async (
  params: DashboardMerchantsSummaryParams,
  signal?: AbortSignal,
): Promise<EntitiesSummary> => {
  return axios
    .get(`${Resources.Dashboard}/top_merchants`, {
      params,
      paramsSerializer,
      signal,
    })
    .then((response: AxiosResponse) => response.data);
};

const getDashboardProvidersSummary = async (
  params: DashboardProvidersSummaryParams,
  signal?: AbortSignal,
): Promise<EntitiesSummary> => {
  return axios
    .get(`${Resources.Dashboard}/top_providers`, {
      params,
      paramsSerializer,
      signal,
    })
    .then((response: AxiosResponse) => response.data);
};

export const legacyDashboardApi: DashboardApi = {
  getTransactionsSummary: getDashboardTransactionsSummary,
  getTransactionsSuccessSummary: getDashboardTransactionsSuccessSummary,
  getTransactionsDynamicSummary: getDashboardTransactionsDynamicSummary,
  getMerchantsSummary: getDashboardMerchantsSummary,
  getProvidersSummary: getDashboardProvidersSummary,
};

const AGGREGATED_DASHBOARD_PATH = `new/${Resources.Dashboard}`;

export const aggregatedDashboardApi: DashboardApi = {
  ...legacyDashboardApi,
  getTransactionsSuccessSummary: (params, signal) =>
    axios
      .get(`${AGGREGATED_DASHBOARD_PATH}/transaction_success_summary`, {
        params,
        paramsSerializer,
        signal,
      })
      .then((response: AxiosResponse) => response.data),

  getMerchantsSummary: (params, signal) =>
    axios
      .get(`${AGGREGATED_DASHBOARD_PATH}/top_merchants`, {
        params,
        paramsSerializer,
        signal,
      })
      .then((response: AxiosResponse) => response.data),

  getProvidersSummary: (params, signal) =>
    axios
      .get(`${AGGREGATED_DASHBOARD_PATH}/top_providers`, {
        params,
        paramsSerializer,
        signal,
      })
      .then((response: AxiosResponse) => response.data),
};
